import React from 'react'
import PropTypes from 'prop-types'
import { User, Mail, Phone, Shield, Clock, Calendar, Edit2 } from 'lucide-react'
import { Modal, Button } from '@components/common'
import './TeamMemberView.scss'

/**
 * Get user initials from name
 */
const getInitials = (firstName, lastName) => {
	const first = firstName?.charAt(0)?.toUpperCase() || ''
	const last = lastName?.charAt(0)?.toUpperCase() || ''
	return first + last || '?'
}

/**
 * Format date for display
 */
const formatDate = (dateString) => {
	if (!dateString) return 'Never'
	const date = new Date(dateString)
	return date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

/**
 * Team Member View Component
 *
 * Displays detailed information about a team member in a modal.
 *
 * @param {Object} user - User data to display
 * @param {Function} onClose - Close modal callback
 * @param {Function} onEdit - Edit button callback
 */
const TeamMemberView = ({ user, onClose, onEdit }) => {
	if (!user) return null

	/**
	 * Get status badge
	 */
	const getStatusBadge = (status) => {
		const statusMap = {
			active: { label: 'Active', className: 'badge--success' },
			inactive: { label: 'Inactive', className: 'badge--secondary' },
			suspended: { label: 'Suspended', className: 'badge--danger' },
		}
		const statusInfo = statusMap[status] || statusMap.inactive
		return <span className={`badge ${statusInfo.className}`}>{statusInfo.label}</span>
	}

	/**
	 * Get role badge
	 */
	const getRoleBadge = (roleSlug, roleName) => {
		const roleColors = {
			'company-admin': 'badge--primary',
			'compliance-manager': 'badge--info',
			'project-manager': 'badge--success',
			'quality-manager': 'badge--warning',
			auditor: 'badge--warning',
		}
		const badgeClass = roleColors[roleSlug] || 'badge--secondary'
		return <span className={`badge ${badgeClass}`}>{roleName || 'Unknown Role'}</span>
	}

	const footer = (
		<>
			<Button variant="secondary" onClick={onClose}>
				Close
			</Button>
			{user.status !== 'suspended' && (
				<Button variant="primary" onClick={onEdit}>
					<Edit2 size={18} />
					<span>Edit Member</span>
				</Button>
			)}
		</>
	)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title="Team Member Details"
			size="medium"
			footer={footer}
			closeOnBackdrop={false}
		>
			<div className="team-member-view">
				{/* Header with Avatar */}
				<div className="team-member-view__header">
					<div className="team-member-view__avatar">
						{user.avatar_url ? (
							<img src={user.avatar_url} alt={`${user.first_name} ${user.last_name}`} />
						) : (
							<div className="team-member-view__avatar-initials">
								{getInitials(user.first_name, user.last_name)}
							</div>
						)}
					</div>
					<div className="team-member-view__title">
						<h2>{`${user.first_name} ${user.last_name}`}</h2>
						<div className="team-member-view__badges">
							{getRoleBadge(user.role_slug, user.role_name)}
							{getStatusBadge(user.status)}
						</div>
					</div>
				</div>

				{/* Contact Information */}
				<div className="team-member-view__section">
					<h3 className="team-member-view__section-title">Contact Information</h3>

					<div className="team-member-view__info-grid">
						<div className="team-member-view__info-item">
							<div className="team-member-view__info-icon">
								<Mail size={18} />
							</div>
							<div className="team-member-view__info-content">
								<span className="team-member-view__info-label">Email</span>
								<span className="team-member-view__info-value">
									<a href={`mailto:${user.email}`}>{user.email}</a>
								</span>
							</div>
						</div>

						<div className="team-member-view__info-item">
							<div className="team-member-view__info-icon">
								<Phone size={18} />
							</div>
							<div className="team-member-view__info-content">
								<span className="team-member-view__info-label">Phone</span>
								<span className="team-member-view__info-value">{user.phone || 'Not provided'}</span>
							</div>
						</div>
					</div>
				</div>

				{/* Role & Access */}
				<div className="team-member-view__section">
					<h3 className="team-member-view__section-title">Role & Access</h3>

					<div className="team-member-view__info-grid">
						<div className="team-member-view__info-item">
							<div className="team-member-view__info-icon">
								<Shield size={18} />
							</div>
							<div className="team-member-view__info-content">
								<span className="team-member-view__info-label">Role</span>
								<span className="team-member-view__info-value">{user.role_name || 'Unknown'}</span>
							</div>
						</div>

						<div className="team-member-view__info-item">
							<div className="team-member-view__info-icon">
								<User size={18} />
							</div>
							<div className="team-member-view__info-content">
								<span className="team-member-view__info-label">Status</span>
								<span className="team-member-view__info-value">{getStatusBadge(user.status)}</span>
							</div>
						</div>
					</div>
				</div>

				{/* Activity */}
				<div className="team-member-view__section">
					<h3 className="team-member-view__section-title">Activity</h3>

					<div className="team-member-view__info-grid">
						<div className="team-member-view__info-item">
							<div className="team-member-view__info-icon">
								<Clock size={18} />
							</div>
							<div className="team-member-view__info-content">
								<span className="team-member-view__info-label">Last Login</span>
								<span className="team-member-view__info-value">{formatDate(user.last_login_at)}</span>
							</div>
						</div>

						<div className="team-member-view__info-item">
							<div className="team-member-view__info-icon">
								<Calendar size={18} />
							</div>
							<div className="team-member-view__info-content">
								<span className="team-member-view__info-label">Member Since</span>
								<span className="team-member-view__info-value">{formatDate(user.created_at)}</span>
							</div>
						</div>
					</div>
				</div>

				{/* Email Verification Status */}
				{user.email_verified_at && (
					<div className="team-member-view__verification">
						<span className="team-member-view__verification-badge badge badge--success">Email Verified</span>
						<span className="team-member-view__verification-date">on {formatDate(user.email_verified_at)}</span>
					</div>
				)}
			</div>
		</Modal>
	)
}

TeamMemberView.propTypes = {
	user: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		first_name: PropTypes.string.isRequired,
		last_name: PropTypes.string.isRequired,
		email: PropTypes.string.isRequired,
		phone: PropTypes.string,
		role_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		role_name: PropTypes.string,
		role_slug: PropTypes.string,
		status: PropTypes.string,
		avatar_url: PropTypes.string,
		last_login_at: PropTypes.string,
		created_at: PropTypes.string,
		email_verified_at: PropTypes.string,
	}),
	onClose: PropTypes.func.isRequired,
	onEdit: PropTypes.func,
}

export default TeamMemberView
